import { validationLength } from "../components/actions/validations";

const codeRule = (max: number) =>
  `Max ${max} characters; letters and numbers only; no spaces or special characters.`;

const nameRule = (max: number) =>
  `Max ${max} characters; letters, numbers and spaces only; no special characters.`;

const slashCodeRule = (max: number) =>
  `Max ${max} characters; letters, numbers and / only; no spaces or other special characters.`;

const otherSlashRule = (max: number) =>
  `Max ${max} characters; letters, numbers, spaces and / only; no other special characters.`;

const integerRule = (max: number) => `Max ${max} digits.`;

const mandatory = (rule: string) => `${rule} Mandatory.`;

/** Shared error helper text for Versa forms. */
export const versaFieldErrors = {
  required: "This field is required.",
  buildingCodes: {
    buildingCode: codeRule(validationLength.buildingCode),
    buildingName: nameRule(validationLength.buildingName),
  },
  category: {
    categoryName: codeRule(validationLength.categoryName),
  },
  department: {
    departmentName: nameRule(validationLength.departmentName),
  },
  houseKeepingSections: {
    houseKeepingSectionCode: codeRule(validationLength.houseKeepingCode),
    houseKeepingSectionName: nameRule(validationLength.houseKeepingName),
    buildingCode: "Select a building code.",
  },
  paymentInformation: {
    paymentCode: codeRule(validationLength.paymentCode),
    paymentName: nameRule(validationLength.paymentName),
    creditLimit: integerRule(validationLength.paymentCreditLimit),
    centralReservation: nameRule(validationLength.centralReservation),
  },
  roomFeatures: {
    roomFeatureCode: codeRule(validationLength.roomFeatureCode),
    roomFeatureName: nameRule(validationLength.roomFeatureName),
  },
  roomLocation: {
    roomLocationCode: slashCodeRule(validationLength.roomTypeCode),
    roomLocationName: otherSlashRule(validationLength.roomTypeName),
  },
  roomTypeDefinitions: {
    roomCode: codeRule(validationLength.roomTypeDefinitionCode),
  },
  roomTypeMaintainence: {
    roomTypeCode: codeRule(validationLength.roomTypeDefinitionCode),
    roomTypeName: nameRule(validationLength.roomTypeDefinitionName),
    resortFee: integerRule(validationLength.resortFee),
  },
  taxes: {
    taxType: codeRule(validationLength.taxType),
  },
  taxExemptCategory: {
    taxExemptCode: codeRule(validationLength.taxExemptCode),
    taxExemptCategoryName: nameRule(validationLength.roomFeatureName),
  },
  taxPostType: {
    postTypeNumber: integerRule(validationLength.postTypeNumber),
    postTypeName: nameRule(validationLength.postTypeName),
    defaultAmount: integerRule(validationLength.defaultAmount),
    defaultFolio: integerRule(validationLength.defaultFolio),
    defaultMasterFolio: integerRule(validationLength.defaultMasterFolio),
    ledgerNumber: nameRule(validationLength.ledgerNumber),
  },
  transportation: {
    listName: codeRule(validationLength.buildingCode),
    transportationType: nameRule(validationLength.roomFeatureName),
    location: nameRule(validationLength.roomFeatureName),
    approxMiles: integerRule(validationLength.paymentCreditLimit),
    approxTime: integerRule(validationLength.paymentCreditLimit),
  },
} as const;

/** Default (non-error) helper text under Versa form fields. */
export const versaFieldHelperText = {
  buildingCodes: {
    buildingCode: mandatory(codeRule(validationLength.buildingCode)),
    buildingName: mandatory(nameRule(validationLength.buildingName)),
  },
  category: {
    categoryName: mandatory(codeRule(validationLength.categoryName)),
  },
  department: {
    departmentName: mandatory(nameRule(validationLength.departmentName)),
  },
  houseKeepingSections: {
    houseKeepingSectionCode: mandatory(
      codeRule(validationLength.houseKeepingCode),
    ),
    houseKeepingSectionName: mandatory(
      nameRule(validationLength.houseKeepingName),
    ),
    buildingCode: mandatory("Select a building code."),
  },
  paymentInformation: {
    paymentCode: mandatory(codeRule(validationLength.paymentCode)),
    paymentName: mandatory(nameRule(validationLength.paymentName)),
    creditLimit: integerRule(validationLength.paymentCreditLimit),
    centralReservation: nameRule(validationLength.centralReservation),
  },
  roomFeatures: {
    roomFeatureCode: mandatory(codeRule(validationLength.roomFeatureCode)),
    roomFeatureName: mandatory(nameRule(validationLength.roomFeatureName)),
  },
  roomLocation: {
    roomLocationCode: mandatory(slashCodeRule(validationLength.roomTypeCode)),
    roomLocationName: mandatory(otherSlashRule(validationLength.roomTypeName)),
  },
  roomTypeDefinitions: {
    roomCode: mandatory(codeRule(validationLength.roomTypeDefinitionCode)),
  },
  roomTypeMaintainence: {
    roomTypeCode: mandatory(codeRule(validationLength.roomTypeDefinitionCode)),
    roomTypeName: mandatory(nameRule(validationLength.roomTypeDefinitionName)),
    resortFee: integerRule(validationLength.resortFee),
  },
  taxes: {
    taxType: mandatory(codeRule(validationLength.taxType)),
    postType: "Create Tax Post Types on the previous screen to add post types.",
  },
  taxExemptCategory: {
    taxExemptCode: mandatory(codeRule(validationLength.taxExemptCode)),
    taxExemptCategoryName: mandatory(
      nameRule(validationLength.roomFeatureName),
    ),
  },
  taxPostType: {
    postTypeNumber: mandatory(integerRule(validationLength.postTypeNumber)),
    postTypeName: mandatory(nameRule(validationLength.postTypeName)),
    defaultAmount: integerRule(validationLength.defaultAmount),
    defaultFolio: integerRule(validationLength.defaultFolio),
    defaultMasterFolio: integerRule(validationLength.defaultMasterFolio),
    ledgerNumber: nameRule(validationLength.ledgerNumber),
  },
  transportation: {
    listName: mandatory(codeRule(validationLength.buildingCode)),
    transportationType: nameRule(validationLength.roomFeatureName),
    location: nameRule(validationLength.roomFeatureName),
    approxMiles: integerRule(validationLength.paymentCreditLimit),
    approxTime: integerRule(validationLength.paymentCreditLimit),
  },
} as const;
